import React, { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Badge,
} from "reactstrap"
import { Form } from "react-bootstrap"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"
import { Link } from "react-router-dom"
import { UncontrolledTooltip } from "reactstrap"
import toastr from "toastr"
import TableContainer from "../../components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import apiURl from "url"

const Customers = () => {
  // Meta title
  document.title = "Customers"

  const navigate = useNavigate()

  // State management
  const [showPassword, setShowPassword] = useState(false)
  const [customers, setCustomers] = useState([])
  const [accountModal, setAccountModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteID, setDeleteID] = useState(null)
  const [editCustomer, setEditCustomer] = useState(null)
  const [formData, setFormData] = useState({
    fullname: "",
    email: "",
    phone: "",
    password: "",
    address: "",
    customerType: "Individual",
    image: null,
  })

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  // Handle form input changes
  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleImageChange = e => {
    const file = e.target.files[0]
    if (file) {
      setFormData(prev => ({ ...prev, image: file }))
    }
  }

  // Fetch all customers
  const fetchAllCustomers = async () => {
    try {
      const response = await fetch(`${apiURl}/api/customers/fetchCustomers`)
      const json = await response.json()
      if (response.status === 200) setCustomers(json.customers.reverse())
    } catch (err) {
      console.error("Fetch Error:", err)
      toastr.error("Failed to fetch customers")
    }
  }

  // Fetch customer data by ID for editing
  const fetchCustomerDataByID = async id => {
    try {
      const response = await fetch(
        `${apiURl}/api/customers/fetchCustomerByID`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id }),
        }
      )
      const json = await response.json()
      if (response.status === 200 && json.status === "success") {
        const customerData = json.CustomerDataByID[0]
        if (customerData) {
          setFormData({
            fullname: customerData.full_name || "",
            email: customerData.email_ || "",
            phone: customerData.phone_ || "",
            password: "", // Password not retrieved for security
            address: customerData.address || "",
            customerType: customerData.customer_type || "Individual",
            image: customerData.image_ || null,
          })
          setEditCustomer(customerData)
          setAccountModal(true)
        }
      }
    } catch (err) {
      console.error("Fetch Customer Error:", err)
      toastr.error("Failed to fetch customer data")
    }
  }

  // Handle save (create or update)
  const handleSaveCustomer = async e => {
    e.preventDefault()

    // Form validation
    if (!formData.fullname.trim()) return toastr.error("Full Name is required")
    if (!formData.email.trim()) return toastr.error("Email is required")
    if (!formData.phone.trim()) return toastr.error("Phone is required")
    if (!formData.address.trim()) return toastr.error("Address is required")
    if (!formData.customerType) return toastr.error("Customer Type is required")
    if (!formData.password.trim() && !editCustomer)
      return toastr.error("Password is required")

    const method = editCustomer ? "PUT" : "POST"
    const url = editCustomer
      ? `${apiURl}/api/customers/edit-customer/${editCustomer.id}`
      : `${apiURl}/api/customers/add-customer`

    const formPayload = new FormData()
    formPayload.append("fullname", formData.fullname)
    formPayload.append("email", formData.email)
    formPayload.append("phone", formData.phone)
    formPayload.append("password", formData.password || "")
    formPayload.append("address", formData.address)
    formPayload.append("customerType", formData.customerType)
    if (formData.image) {
      formPayload.append("image", formData.image)
    }

    try {
      const response = await fetch(url, {
        method,
        body: formPayload,
      })

      const json = await response.json()

      if (response.ok) {
        toastr.success(editCustomer ? "Customer updated!" : "Customer added!")
        fetchAllCustomers()
        setAccountModal(false)
        setEditCustomer(null)
        setFormData({
          fullname: "",
          email: "",
          phone: "",
          password: "",
          address: "",
          customerType: "Individual",
          image: null,
        })
      } else {
        toastr.error(json.message || "Failed to save customer")
      }
    } catch (err) {
      console.error("Save Error:", err)
      toastr.error("Failed to save customer")
    }
  }

  // Handle delete
  const deleteCustomer = async () => {
    try {
      const response = await fetch(
        `${apiURl}/api/customers/delete-customer/${deleteID}`,
        {
          method: "DELETE",
        }
      )
      if (response.ok) {
        toastr.success("Customer deleted successfully")
        setDeleteModal(false)
        fetchAllCustomers()
      } else {
        toastr.error("Failed to delete customer")
      }
    } catch (err) {
      console.error("Delete Error:", err)
      toastr.error("Failed to delete customer")
    }
  }

  // Initialize component
  useEffect(() => {
    fetchAllCustomers()
  }, [])

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: "Full Name",
        accessor: "full_name",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Email",
        accessor: "email_",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Phone",
        accessor: "phone_",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Address",
        accessor: "address",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Customer Type",
        accessor: "customer_type",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Status",
        accessor: "status_",
        Cell: ({ row }) => {
          const isActive = row.original.status_ === "Active"
          const handleToggle = async () => {
            const newStatus = isActive ? "Not Active" : "Active"
            try {
              const res = await fetch(
                `${apiURl}/api/customers/update-status/${row.original.id}`,
                {
                  method: "PUT",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ status_: newStatus }),
                }
              )
              if (res.ok) {
                toastr.success("Status updated successfully")
                fetchAllCustomers()
              } else {
                toastr.error("Failed to update status")
              }
            } catch (err) {
              toastr.error("Server error")
              console.error(err)
            }
          }
          return (
            <Form>
              <Form.Check
                type="switch"
                id={`status-switch-${row.original.id}`}
                label={
                  isActive ? (
                    <Badge className="bg-success">Active</Badge>
                  ) : (
                    <Badge className="bg-danger">Inactive</Badge>
                  )
                }
                defaultChecked={isActive}
                onChange={handleToggle}
                style={{
                  color: isActive ? "green" : "red",
                  fontWeight: "bold",
                }}
              />
            </Form>
          )
        },
      },
      {
        Header: "Action",
        accessor: "id",
        Cell: cellProps => (
          <div className="d-flex gap-3">
            <Link
              to="#"
              onClick={() => fetchCustomerDataByID(cellProps.value)}
              className="text-primary"
            >
              <i
                className="mdi mdi-eye font-size-18"
                id={`viewtooltip-${cellProps.value}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`viewtooltip-${cellProps.value}`}
              >
                View
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-danger"
              onClick={() => {
                setDeleteID(cellProps.value)
                setDeleteModal(true)
              }}
            >
              <i
                className="mdi mdi-delete font-size-18"
                id={`deletetooltip-${cellProps.value}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`deletetooltip-${cellProps.value}`}
              >
                Delete
              </UncontrolledTooltip>
            </Link>
          </div>
        ),
      },
    ],
    []
  )

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={deleteCustomer}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="6">
              <h5 className="mt-2">Customers</h5>
            </Col>
            <Col>
              <div className="float-end">
                <Button
                  color="primary"
                  className="btn-sm w-md"
                  onClick={() => {
                    setFormData({
                      fullname: "",
                      email: "",
                      phone: "",
                      password: "",
                      address: "",
                      customerType: "Individual",
                      image: null,
                    })
                    setEditCustomer(null)
                    setAccountModal(true)
                  }}
                >
                  <i className="fa fa-fw fa-plus me-1" />
                  Create Customer
                </Button>
              </div>
            </Col>
          </Row>

          <Row className="pt-3">
            <Col lg="12">
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={customers}
                    isGlobalFilter={true}
                    isAddOptions={false}
                    className="custom-table"
                    customPageSize={10}
                    FilterColumns={false}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Customer Modal */}
      <Modal
        isOpen={accountModal}
        toggle={() => setAccountModal(!accountModal)}
        size="lg"
        centered
      >
        <Form onSubmit={handleSaveCustomer}>
          <ModalHeader toggle={() => setAccountModal(!accountModal)}>
            {editCustomer ? "Edit Customer" : "Create New Customer"}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Full Name *</Label>
                  <Input
                    type="text"
                    name="fullname"
                    value={formData.fullname}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Phone *</Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Password *</Label>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                      required={!editCustomer}
                    />
                    <InputGroupText
                      onClick={togglePasswordVisibility}
                      style={{ cursor: "pointer" }}
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEyeSlash : faEye}
                      />
                    </InputGroupText>
                  </InputGroup>
                </FormGroup>
                <FormGroup>
                  <Label>Profile Image (Optional)</Label>
                  <Input
                    type="file"
                    name="image"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </FormGroup>

                {formData.image && (
                  <div className="mb-3">
                    <img
                      src={
                        typeof formData.image === "string"
                          ? `${apiURl}${formData.image}`
                          : URL.createObjectURL(formData.image)
                      }
                      alt="Preview"
                      style={{
                        width: "70px",
                        height: "70px",
                        objectFit: "cover",
                        borderRadius: "5px",
                      }}
                    />
                  </div>
                )}
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Address *</Label>
                  <Input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter address"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Customer Type *</Label>
                  <Input
                    type="select"
                    name="customerType"
                    value={formData.customerType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Individual">Individual</option>
                    <option value="Business">Business</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => {
                setFormData({
                  fullname: "",
                  email: "",
                  phone: "",
                  password: "",
                  address: "",
                  customerType: "Individual",
                  image: null,
                })
                setEditCustomer(null)
                setAccountModal(false)
              }}
            >
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {editCustomer ? "Update Customer" : "Create Customer"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  )
}

export default Customers
